import { Container, Section } from '@radix-ui/themes'
import { type MetaArgs, type MetaFunction } from '@remix-run/node'
import {
	type ShouldRevalidateFunctionArgs,
	useLoaderData,
} from '@remix-run/react'
import isEqual from 'lodash.isequal'
import uniqWith from 'lodash.uniqwith'
import * as React from 'react'
import { ClientOnly } from "remix-utils/client-only";

import { appConfig } from '~/app-config.ts'
import { Image, IMAGE_FILTERS } from '~/components/ui/image'
import { type GetProduct } from '~/models/ecommerce-providers/sylius/shop/fetch/product/get-product'
import { type Locale } from '~/models/locale'
import { type loader } from '~/routes/($lang)._index/index.server'
import { Partners } from '~/routes/($lang)._index/partners'
import { ProductsSlider } from '~/components/common/products-slider.tsx'
import { NEWSLETTER_FORM } from '~/routes/($lang).newsletter/newsletter.component'
import { ROOT_INPUTS } from '~/routes/_layout/root.component'
import { type loader as RouteLoader } from '~/routes/_layout/root.server'
import { defaultMetaI18next } from '~/utils/client/default-meta-i18next.ts'
import { imagePathFilter } from '~/utils/image-path-filter.ts'
import { Services } from '~/routes/($lang)._index/services.tsx'
import { Offer } from '~/routes/($lang)._index/offer.tsx'
import { Gallery } from '~/routes/($lang)._index/gallery.tsx'
import parse from 'html-react-parser'
import { useTranslation } from 'react-i18next'

/* -------------------------------------------------------------------------------------------------
 * I18next
 * -----------------------------------------------------------------------------------------------*/

enum I18NEXT {
	routeIndex = 'route-_index',
}

/* -------------------------------------------------------------------------------------------------
 * Route loader
 * -----------------------------------------------------------------------------------------------*/

enum ROUTE_LOADER {
	root = 'root',
	current = 'routes/($lang)._index',
}

/* -------------------------------------------------------------------------------------------------
 * Handle
 * -----------------------------------------------------------------------------------------------*/

const handle = { i18n: [...Object.values(I18NEXT)] }

/* -------------------------------------------------------------------------------------------------
 * Meta
 * -----------------------------------------------------------------------------------------------*/

const meta: MetaFunction<
	typeof loader,
	{
		[ROUTE_LOADER.root]: typeof RouteLoader
	}
> = ({ data, matches }: MetaArgs) => {
	const parentMeta = matches.reduce((a, b) =>
		a.meta.length > b.meta.length ? a : b,
	).meta
	const title = parentMeta.find((meta) => 'title' in meta)
	const description = parentMeta.find(
		(meta) => 'name' in meta && meta.name === 'description',
	)

	const rootData = matches.find((match) => match.id === ROUTE_LOADER.root)!
		.data as { locale: Locale }

	// @ts-ignore
	return uniqWith(
		title?.title.includes('Error')
			? [...parentMeta]
			: data
				? [
						...parentMeta.filter(
							(meta) =>
								!Object.entries(meta).find(
									([key, value]) =>
										key === 'title' ||
										(key === 'name' && value === 'description'),
								),
						),
						// @ts-ignore
						{ title: `${data.meta.title} | ${title?.title}` },
						{
							name: 'description',
							// @ts-ignore
							content: `${data.meta.description} | ${description?.content}`,
						},
						// Preload images
						// @ts-ignore
						...(
							data.products?.items?.filter(
								({ images }: GetProduct) => !!images?.[0]?.path,
							) || []
						).map(({ images }: GetProduct) => ({
							tagname: 'link',
							rel: 'preload',
							href: `${process.env.NODE_ENV === 'development' ? appConfig.url_dev_api : ''}${imagePathFilter({ path: images![0].path!, filter: IMAGE_FILTERS.gridFilter })}`,
							as: 'image',
						})),
						// I18next
						...defaultMetaI18next<typeof I18NEXT>({
							I18NEXT,
							locale: rootData.locale,
						}),
					]
				: [
						...parentMeta.filter(
							(meta) =>
								!Object.entries(meta).find(
									([key, value]) =>
										key === 'title' ||
										(key === 'name' && value === 'description'),
								),
						),
						// @ts-ignore
						{ title: `Error | ${title?.title}` },
						{
							name: 'description',
							// @ts-ignore
							content: `Error | ${description?.content}`,
						},
					],
		(a, b) => isEqual(a, b),
	)
}

/* -------------------------------------------------------------------------------------------------
 * Should revalidate
 * -----------------------------------------------------------------------------------------------*/

const shouldRevalidate = ({
	formData,
	defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) => {
	if (formData?.get(ROOT_INPUTS.subaction) === NEWSLETTER_FORM.subaction) {
		return false
	}

	return defaultShouldRevalidate
}

/* -------------------------------------------------------------------------------------------------
 * Component
 * -----------------------------------------------------------------------------------------------*/

const Component: React.FC = () => {
	const { products } = useLoaderData<typeof loader>()
  const { t } = useTranslation(I18NEXT.routeIndex)

	return (
    <>
      <div className="flex flex-col items-center mt-24">
        <div className="flex justify-center">
          <Image
            src="/pwa/img/compress/img_8-3.webp"
            api={false}
            alt="header"
            className="brightness-[.85]"
          />
        </div>
      </div>
      <ProductsSlider
        products={products?.items}
        title={t(`common.productSlider.title`)}
        subtitle={t(`common.productSlider.subtitle`)}
      />

      <Container px="5">
        <Services />
      </Container>

      <Section id="offer" className="app-bg-grad">
        <Container px="5" my="9">
          <Offer />
        </Container>
      </Section>

      <Container px="5" mt="9">
        <ClientOnly fallback={null}>
          {() => <Gallery/>}
        </ClientOnly>
        <Partners/>
      </Container>
    </>
  )
}
Component.displayName = 'Component'

/* -----------------------------------------------------------------------------------------------*/

export {
  Component,
  shouldRevalidate,
  handle,
  meta,
  I18NEXT as INDEX_I18NEXT,
  ROUTE_LOADER as INDEX_ROUTE_LOADER,
}
